import { useEffect, useState } from 'react'
import { useDataSourceStore } from '@/stores/dataSourceStore'
import type { DataSourceDetails } from '@/services/apiClient'

/**
 * Hook to get datasource data from the centralized store
 * This replaces direct usage of useDataSourceQuery to prevent duplicate API calls
 */
export function useDataSourceFromStore(id: string) {
  const { 
    dataSources, 
    loading, 
    errors, 
    fetchDataSource 
  } = useDataSourceStore()
  
  const [data, setData] = useState<DataSourceDetails | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  
  useEffect(() => {
    if (!id) {
      setData(undefined)
      setIsLoading(false)
      setError(null)
      return
    }
    
    // Get current state from store
    const cachedData = dataSources.get(id)
    const loadingState = loading.get(id) || false
    const errorState = errors.get(id) || null
    
    setData(cachedData)
    setIsLoading(loadingState)
    setError(errorState)
    
    // Fetch if not cached and not loading and there is no prior error
    // This prevents infinite re-fetch loops when the API returns errors (e.g., 500)
    if (!cachedData && !loadingState && !errorState) {
      fetchDataSource(id).catch(() => {
        // Error is handled in the store
      })
    }
  }, [id, dataSources, loading, errors, fetchDataSource])
  
  return {
    data,
    isLoading,
    error,
    // ensure the function is explicitly async to satisfy lint rule
    refetch: async () => fetchDataSource(id)
  }
}